import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'src/@vymo/ui/atoms/button';
import Modal, { Body, Footer, Header } from 'src/@vymo/ui/blocks/modal';
import OtpWithoutCTA from 'src/@vymo/ui/molecules/otp/input/otpWithoutCTA';
import { Keys, locale } from 'src/i18n';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { closePortal } from 'src/workspace/utils';
import { VERIFICATION_EXHAUST_ERROR_CODE } from './constant';
import {
  getFormAuthData,
  getFormAuthError,
  getFormAuthErrorCode,
  getFormAuthLoading,
  getFormAuthResendTimer,
  getFormAuthSuccess,
} from './selector';
import { clearFormAuthState } from './slice';
import { sendOtp, verifyOtp } from './thunk';
import { FormAuthProps } from './types';
import styles from './index.module.scss';

function FormAuth({ onClose, callback, payload }: FormAuthProps) {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getFormAuthLoading);
  const success = useAppSelector(getFormAuthSuccess);
  const requestError = useAppSelector(getFormAuthError);
  const errorCode = useAppSelector(getFormAuthErrorCode);
  const requestData = useAppSelector(getFormAuthData);
  const resolvedResendTimer = useAppSelector(getFormAuthResendTimer);
  const resolvedNumOfBoxes = requestData?.otp_digits ?? 6;
  const resolvedOtpResendsRemaining =
    requestData?.resend_attempts_remaining ?? 0;
  const resolvedVerificationAttemptsRemaining =
    requestData?.verification_attempts_remaining ?? 0;
  const resolvedTitle = 'Verify';
  const resolvedMessage = requestData?.title || requestData?.message;
  const [otpValue, setOtpValue] = useState<string[]>(
    Array(resolvedNumOfBoxes).fill(''),
  );

  useEffect(() => {
    dispatch(sendOtp(payload));
  }, [dispatch, payload]);

  useEffect(() => {
    setOtpValue(Array(resolvedNumOfBoxes).fill(''));
  }, [resolvedNumOfBoxes]);

  useEffect(
    () => () => {
      dispatch(clearFormAuthState());
    },
    [dispatch],
  );

  const closeModal = useCallback(
    (isVerified = false) => {
      dispatch(clearFormAuthState());
      callback?.(isVerified);
      onClose?.();
      closePortal?.();
    },
    [callback, dispatch, onClose],
  );

  useEffect(() => {
    if (success) {
      closeModal(true);
    }
  }, [closeModal, success]);

  const handleResend = useCallback(() => {
    dispatch(sendOtp(payload));
  }, [dispatch, payload]);

  const handleSubmit = useCallback(() => {
    dispatch(
      verifyOtp({
        ...(payload ?? {}),
        otp: otpValue.join(''),
      }),
    );
  }, [dispatch, otpValue, payload]);

  const isSubmitDisabled = useMemo(
    () =>
      otpValue.some((digit) => !digit) ||
      loading ||
      resolvedOtpResendsRemaining <= 0 ||
      errorCode === VERIFICATION_EXHAUST_ERROR_CODE,
    [loading, otpValue, resolvedOtpResendsRemaining, errorCode],
  );

  return (
    <Modal
      open
      onClose={() => closeModal(false)}
      data-test-id="form-auth-modal"
      classNames={styles.modal}
    >
      <Header data-test-id="form-auth-modal-header">{resolvedTitle}</Header>
      <Body data-test-id="form-auth-modal-body">
        <OtpWithoutCTA
          data-test-id="form-auth-otp"
          onResend={handleResend}
          numOfBoxes={resolvedNumOfBoxes}
          resendTimer={resolvedResendTimer}
          error={requestError || ''}
          message={resolvedMessage}
          otpResendsRemaining={resolvedOtpResendsRemaining}
          verificationAttemptsRemaining={resolvedVerificationAttemptsRemaining}
          onChange={setOtpValue}
          value={otpValue}
        />
      </Body>
      <Footer data-test-id="form-auth-modal-footer">
        <Button
          data-test-id="form-auth-submit"
          type="primary"
          disabled={isSubmitDisabled}
          loading={loading}
          onClick={handleSubmit}
        >
          {locale(Keys.SUBMIT)}
        </Button>
      </Footer>
    </Modal>
  );
}

export default FormAuth;
